/**
 * Who is in a session, as one pure table.
 *
 * A peer id is a long opaque string the network mints fresh on every join. A player id is a small
 * stable number the simulation, the wire and the scoreboard all use. This is the one place the two
 * are bridged, and it is deliberately pure: seats decide the four-player cap and survive a player
 * dropping and coming back, which are exactly the rules worth testing without a network.
 *
 * A seat outlives a connection. When somebody drops, the seat is held rather than freed: their peer
 * id goes, their player id and their token stay, and for as long as the hold lasts they are the
 * only one who can sit back down. That is what makes a reconnect the same player, not a new one,
 * and it is why the cap counts seats rather than live peers. A fifth player arriving while a fourth
 * is walking back is refused, which is the honest answer.
 *
 * The token is the identity, not the peer id. A returning player proves who it is by presenting the
 * token the host gave it, because the peer id it had is gone the moment the connection was.
 *
 * Nothing here mints a token or reads a clock: both arrive as arguments, so the table stays a pure
 * function of what it is told.
 */

import { BASELINE_STATS } from '@/sim/combat/statContract';
import { HOST_PLAYER_ID, MAX_PLAYERS } from '@/net/session/sessionLimits';
import { newSheet, sheetsEqual } from '@/sim/progression/characterSheet';
import type { CharacterSheet } from '@/sim/progression/characterSheet';
import type { PeerId } from '@/net/transportContract';

export interface Seat {
  readonly playerId: number;
  /** Null while the seat is held for somebody who dropped. */
  readonly peerId: PeerId | null;
  readonly name: string;
  /**
   * Who is playing in this seat, or null while they have not said yet.
   *
   * Null is a real state rather than a missing value: a seat is claimed the moment a peer is
   * announced, and the announcement carrying the character takes a round trip to arrive. A mode
   * that puts a body in the world reads this to know whether it has anything to build one from.
   */
  readonly sheet: CharacterSheet | null;
  readonly ready: boolean;
  /** What a returning player presents to get this seat back. Empty for the host's own seat. */
  readonly token: string;
  /** Local milliseconds after which a held seat is freed. Null while somebody is sitting in it. */
  readonly heldUntilMs: number | null;
}

export interface SeatTable {
  readonly seats: readonly Seat[];
}

export interface SeatOccupancy {
  /** Seats with somebody connected in them. */
  readonly taken: number;
  /** Seats being kept for somebody who dropped. */
  readonly held: number;
  readonly free: number;
}

/**
 * What a player is called before it has said.
 *
 * A name is rolled on the machine it belongs to and announced, so there is always a moment where a
 * seat exists and its name has not arrived yet. This fills it. It lives here rather than in a
 * surface because it goes on the wire in the roster, so a guest and the host have to produce the
 * same string for the same seat.
 */
export function defaultSeatName(playerId: number): string {
  return `Player ${String(playerId)}`;
}

/**
 * What a player is playing as before they have said.
 *
 * The counterpart to `defaultSeatName`, and here for the same reason: it goes on the wire in the
 * roster, so a guest and the host have to produce the same sheet for the same seat. It is the
 * untrained body every character starts from, which is what that player would have had.
 */
export const DEFAULT_SEAT_SHEET: CharacterSheet = newSheet(BASELINE_STATS);

/** A session with only its host in it. The host is always ready, and never holds a token. */
export function createSeatTable(
  hostPeerId: PeerId,
  hostName?: string,
  hostSheet?: CharacterSheet,
): SeatTable {
  return {
    seats: [
      {
        playerId: HOST_PLAYER_ID,
        peerId: hostPeerId,
        name: hostName ?? defaultSeatName(HOST_PLAYER_ID),
        sheet: hostSheet ?? null,
        ready: true,
        token: '',
        heldUntilMs: null,
      },
    ],
  };
}

/** Null when the session is full. A held seat is full, which is the point of holding it. */
export function claim(table: SeatTable, peerId: PeerId, token: string): SeatClaim | null {
  if (table.seats.length >= MAX_PLAYERS) return null;

  const playerId = freePlayerId(table);
  const seat: Seat = {
    playerId,
    peerId,
    name: defaultSeatName(playerId),
    sheet: null,
    ready: false,
    token,
    heldUntilMs: null,
  };
  return { table: { seats: inAscendingOrder([...table.seats, seat]) }, seat };
}

export interface SeatClaim {
  readonly table: SeatTable;
  readonly seat: Seat;
}

/**
 * Puts a returning player back in its own seat, on the strength of its token.
 *
 * The seat does not have to be held for this to work. A guest can be back before the host has even
 * noticed it left, because a dead connection takes seconds to be declared dead, and refusing the
 * token then would hand that player a second body.
 *
 * Whatever seat this peer was given on the way in goes. A connection is announced before the token
 * on it arrives, so a returning player is always seated as a newcomer first and only then says who
 * it was. Leaving both standing is a row nobody is in, counting against the four and drawn in
 * everybody's player list.
 */
export function reclaim(table: SeatTable, peerId: PeerId, token: string): SeatClaim | null {
  if (token === '') return null;
  const found = table.seats.find((seat) => seat.token === token);
  if (found === undefined) return null;

  const seat: Seat = { ...found, peerId, heldUntilMs: null };
  const others: SeatTable = {
    seats: table.seats.filter(
      (existing) => existing.playerId === found.playerId || existing.peerId !== peerId,
    ),
  };
  return { table: replace(others, seat), seat };
}

/** Keeps a seat for whoever just dropped out of it. Unknown peers are nobody's seat. */
export function hold(table: SeatTable, peerId: PeerId, untilMs: number): SeatTable {
  const found = seatOfPeer(table, peerId);
  if (found === null || found.token === '') return table;
  return replace(table, { ...found, peerId: null, ready: false, heldUntilMs: untilMs });
}

/** Gives a seat up for good, for a player who said it was leaving. */
export function release(table: SeatTable, peerId: PeerId): SeatTable {
  const found = seatOfPeer(table, peerId);
  if (found === null || found.token === '') return table;
  return { seats: table.seats.filter((seat) => seat.playerId !== found.playerId) };
}

export interface SeatExpiry {
  readonly table: SeatTable;
  readonly expired: readonly number[];
}

/** Frees every hold that has run out, and names who lost their seat. */
export function expire(table: SeatTable, nowMs: number): SeatExpiry {
  const expired = table.seats
    .filter((seat) => seat.heldUntilMs !== null && seat.heldUntilMs <= nowMs)
    .map((seat) => seat.playerId);

  if (expired.length === 0) return { table, expired };
  return {
    table: { seats: table.seats.filter((seat) => !expired.includes(seat.playerId)) },
    expired,
  };
}

/**
 * Changes what somebody is called, on their own say-so.
 *
 * Keyed by peer id, so a seat can only ever be renamed by whoever is sitting in it: a held seat has
 * no peer and cannot be renamed by anybody, which is what stops a name being taken over while its
 * owner is walking back to it. An empty name is refused rather than published, because a blank row
 * in the player list is worse than the number it replaced.
 */
export function rename(table: SeatTable, peerId: PeerId, name: string): SeatTable {
  const found = seatOfPeer(table, peerId);
  if (found === null || name === '' || found.name === name) return table;
  return replace(table, { ...found, name });
}

/**
 * Says who is sitting in a seat, on their own say-so.
 *
 * Keyed by peer id for the same reason `rename` is: a held seat has nobody in it, so nobody can
 * swap the character it is being kept for while its owner is walking back to it. Unlike a name this
 * is not cosmetic, so there is no fallback for a refusal: a seat with no sheet stays a seat with no
 * sheet, and whatever was going to build a body from it waits.
 */
export function announceSheet(table: SeatTable, peerId: PeerId, sheet: CharacterSheet): SeatTable {
  const found = seatOfPeer(table, peerId);
  if (found === null || (found.sheet !== null && sheetsEqual(found.sheet, sheet))) return table;
  return replace(table, { ...found, sheet });
}

export function setReady(table: SeatTable, peerId: PeerId, ready: boolean): SeatTable {
  const found = seatOfPeer(table, peerId);
  if (found === null || found.ready === ready) return table;
  return replace(table, { ...found, ready });
}

export function seatOfPeer(table: SeatTable, peerId: PeerId): Seat | null {
  return table.seats.find((seat) => seat.peerId === peerId) ?? null;
}

export function seatOfPlayer(table: SeatTable, playerId: number): Seat | null {
  return table.seats.find((seat) => seat.playerId === playerId) ?? null;
}

export function occupancy(table: SeatTable): SeatOccupancy {
  const held = table.seats.filter((seat) => seat.heldUntilMs !== null).length;
  return {
    taken: table.seats.length - held,
    held,
    free: MAX_PLAYERS - table.seats.length,
  };
}

/**
 * The lowest number nobody has.
 *
 * Lowest rather than next, so a session that has churned all evening still numbers its players one
 * to four instead of counting up to eleven with three people in the room.
 */
function freePlayerId(table: SeatTable): number {
  let candidate = HOST_PLAYER_ID + 1;
  while (table.seats.some((seat) => seat.playerId === candidate)) candidate += 1;
  return candidate;
}

function replace(table: SeatTable, seat: Seat): SeatTable {
  return {
    seats: table.seats.map((existing) => (existing.playerId === seat.playerId ? seat : existing)),
  };
}

/**
 * Seats stay in ascending player order.
 *
 * A player leaving frees a number in the middle, and the next arrival takes it, so appending would
 * put seat two after seat three. Order is what makes a roster frame the same bytes for the same
 * session, and it is what stops a player list reshuffling itself as people come and go.
 */
function inAscendingOrder(seats: readonly Seat[]): readonly Seat[] {
  return [...seats].sort((left, right) => left.playerId - right.playerId);
}
